import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { bankApi, accountApi, clientApi } from '@/services/api'
import { formatCurrency } from '@/utils/currency'
import { useClientStore } from '@/stores/clientStore'
import type { BankAccount, Account } from '@/types'
import { Plus, Upload, GitCompare, Building2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

export default function BankAccounts() {
  const { clientId } = useParams<{ clientId: string }>()
  const currentClient = useClientStore((s) => s.currentClient)
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({
    name: '', account_number: '', bank_name: '', currency: 'USD',
    account_id: '', opening_balance: '0',
  })

  const clientCurrency = currentClient?.currency || 'USD'

  function handleOpenAdd() {
    setForm({ name: '', account_number: '', bank_name: '', currency: clientCurrency, account_id: '', opening_balance: '0' })
    setShowAdd(true)
  }

  useEffect(() => {
    if (!clientId) return
    clientApi.get(clientId).then((res) => setCurrentClient(res.data.client))
    loadData()
  }, [clientId])

  async function loadData() {
    setLoading(true)
    try {
      const [bankRes, accRes] = await Promise.all([
        bankApi.list(clientId!),
        accountApi.list(clientId!),
      ])
      setBankAccounts(bankRes.data.bank_accounts)
      setAccounts(accRes.data.accounts.filter((a: Account) => a.account_type === 'asset'))
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    try {
      await bankApi.create({ client_ledger_id: clientId!, ...form })
      toast.success('Bank account created')
      setShowAdd(false)
      setForm({ name: '', account_number: '', bank_name: '', currency: clientCurrency, account_id: '', opening_balance: '0' })
      loadData()
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed'
      toast.error(message)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-9 w-36" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-44 rounded-xl" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Bank Accounts</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Manage bank accounts and import statements</p>
        </div>
        <Button variant="create" onClick={handleOpenAdd}>
          <Plus className="w-4 h-4" /> Add Bank Account
        </Button>
      </div>

      {/* Add form */}
      {showAdd && (
        <Card>
          <CardHeader className="border-b px-5 py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[13px] font-bold text-[#000A1E]">New Bank Account</CardTitle>
              <Button variant="ghost" size="icon-sm" onClick={() => { setShowAdd(false) }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-5">
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Account Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Main Business Account" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Bank Name</Label>
                  <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                    placeholder="e.g. ANZ, Commonwealth" />
                </div>
                <div className="space-y-1.5">
                  <Label>Account Number</Label>
                  <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                    placeholder="Optional" />
                </div>
                <div className="space-y-1.5">
                  <Label>Linked GL Account</Label>
                  <select value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                    className="input" required>
                    <option value="">Select account...</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <div className="input flex items-center text-[rgba(0,10,30,0.75)] bg-muted/50 cursor-not-allowed select-none">
                    {clientCurrency}
                    <span className="ml-2 text-xs">— inherited from client</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Opening Balance</Label>
                  <Input type="number" step="0.01" value={form.opening_balance}
                    onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
                </div>
              </div>
              <Separator />
              <div className="flex gap-2">
                <Button type="submit" variant="create">Create Account</Button>
                <Button type="button" variant="outline" onClick={() => { setShowAdd(false) }}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Bank account cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {bankAccounts.map((ba) => (
          <Card key={ba.id} className="hover:shadow-card-hover transition-shadow duration-200">
            <CardContent className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="min-w-0">
                  <h3 className="text-[13px] font-bold text-[#000A1E] truncate">{ba.name}</h3>
                  <p className="text-[13px] text-[rgba(0,10,30,0.75)]">{ba.bank_name || 'Bank not specified'}</p>
                </div>
              </div>
              {ba.account_number && (
                <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-3 font-mono">
                  ****{ba.account_number.slice(-4)}
                </p>
              )}
              <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-4">
                Opening balance: <span className="font-medium text-[#000A1E]">{formatCurrency(ba.opening_balance, ba.currency)}</span>
              </p>
            </CardContent>
            <CardFooter className="gap-2">
              <Link
                to={`/clients/${clientId}/banking/${ba.id}/import`}
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'flex-1 justify-center gap-1.5')}
              >
                <Upload className="w-3.5 h-3.5" /> Import
              </Link>
              <Link
                to={`/clients/${clientId}/banking/${ba.id}/reconcile`}
                className={cn(buttonVariants({ variant: 'default', size: 'sm' }), 'flex-1 justify-center gap-1.5')}
              >
                <GitCompare className="w-3.5 h-3.5" /> Reconcile
              </Link>
            </CardFooter>
          </Card>
        ))}
        {bankAccounts.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="p-12 text-center">
              <Building2 className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
              <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-1">No bank accounts yet</p>
              <p className="text-[13px] text-[rgba(0,10,30,0.75)]/70">Add a bank account to start importing statements</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
